import {PropertyStrategyComponentType} from '@toolify/client/src/modules/tableItems/enum/PropertyStrategyComponentType'
import {
  INumberPropertyTypeInterfaces,
  NumberRoundTypes,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/NumberPropertyTypeStrategy/types/INumberPropertyTypeInterfaces'
import {
  INumberBin,
  isNumberBin,
} from '@toolify/server/src/services/DatabasePropertyService/strategies/ColumnTypeStrategies/NumberPropertyTypeStrategy/numberBin'
import {FilterItemType} from '@toolify/server/src/services/FilterService/enum/FilterItemType'
import {FilterGroupOperator} from '@toolify/server/src/services/FilterService/enum/FilterGroupOperator'
import {FilterPropertyOperator} from '@toolify/server/src/services/FilterService/enum/FilterPropertyOperator'
import {FilterPropertyPayloadType} from '@toolify/server/src/services/FilterService/enum/FilterPropertyPayloadType'
import {IDatabasePropertyResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDatabasePropertyResponseInstance'
import {IFilter} from '@toolify/server/src/services/FilterService/types/IFilter'
import {DatabaseViewType} from '@toolify/server/src/models/mongoose/DatabaseViewModel/enum/DatabaseViewType'
import {GroupSortModeType} from '@toolify/server/src/services/DatabasePropertyService/enum/GroupSortModeType'
import {GroupGranularityType} from '@toolify/server/src/services/DatabasePropertyService/types/GroupGranularityType'
import {COMMON_GROUP_SORT_LABEL_KEYS} from '@toolify/client/src/modules/databases/utils/groupSortModes'
import {
  getNumberBinSize,
  isCustomNumberGranularity,
  numberGroupGranularityDescriptor,
} from '@toolify/client/src/modules/databases/utils/numberGroupGranularityDescriptor'

const numericGroupSort = {
  availableModes: [GroupSortModeType.ValueAsc, GroupSortModeType.ValueDesc, GroupSortModeType.CountDesc, GroupSortModeType.CountAsc],
  defaultMode: GroupSortModeType.ValueAsc,
  getLabelKey: (mode: GroupSortModeType): string => {
    if(mode === GroupSortModeType.ValueAsc) return 'database.header.groupSort.numericAsc'
    if(mode === GroupSortModeType.ValueDesc) return 'database.header.groupSort.numericDesc'
    return COMMON_GROUP_SORT_LABEL_KEYS[mode]
  },
}

const NUMBER_BIN_LABEL_SEPARATOR = ' – '
const NUMBER_BIN_OPEN_BELOW_PREFIX = '< '
const NUMBER_BIN_OPEN_ABOVE_SUFFIX = '+'

export function useNumberValueTypeStrategy() {
  const strategyComponentType = PropertyStrategyComponentType.Input

  const formatNumber = (value: number, propertyOptions: INumberPropertyTypeInterfaces['options'] | null | undefined, shouldFormat: boolean) => {
    if(!propertyOptions) {
      return value
    }
    let displayValue = value
    const prefix = propertyOptions.prefix || ''
    const suffix = propertyOptions.suffix || ''
    const roundDigits = propertyOptions.roundDigits || 0
    const roundFactor = 10 ** roundDigits
    const roundFn = {
      [NumberRoundTypes.Up]: Math.ceil,
      [NumberRoundTypes.Down]: Math.floor,
      [NumberRoundTypes.Auto]: Math.round,
    }[propertyOptions.roundType]
    if(roundFn) {
      displayValue = roundFn(displayValue * roundFactor) / roundFactor
      displayValue = Number(displayValue.toFixed(roundDigits))
    }

    if(shouldFormat) {
      return `${prefix}${displayValue}${suffix}`
    }
    return displayValue
  }

  const formatNumberBin = (bin: INumberBin, propertyOptions: INumberPropertyTypeInterfaces['options'] | null | undefined, shouldFormat: boolean): string => {
    if(bin.from === null) {
      return `${NUMBER_BIN_OPEN_BELOW_PREFIX}${formatNumber(bin.to, propertyOptions, shouldFormat)}`
    }
    if(bin.to === null) {
      return `${formatNumber(bin.from, propertyOptions, shouldFormat)}${NUMBER_BIN_OPEN_ABOVE_SUFFIX}`
    }
    return `${formatNumber(bin.from, propertyOptions, shouldFormat)}${NUMBER_BIN_LABEL_SEPARATOR}${formatNumber(bin.to, propertyOptions, shouldFormat)}`
  }

  const getDisplayValue = (value: INumberPropertyTypeInterfaces['fullValue'] | INumberBin, propertyOptions: INumberPropertyTypeInterfaces['options'], shouldFormat = true) => {
    if(isNumberBin(value)) {
      return formatNumberBin(value, propertyOptions, shouldFormat)
    }
    if(!propertyOptions || isNaN(Number(value)) || value === null || value === undefined) {
      return value
    }
    return formatNumber(value, propertyOptions, shouldFormat)
  }

  const fullValueToText = (value: INumberPropertyTypeInterfaces['fullValue'] | INumberBin, property?: IDatabasePropertyResponseInstance) => {
    if(isNumberBin(value)) {
      return String(getDisplayValue(value, property?.valueTypeOptions as INumberPropertyTypeInterfaces['options']))
    }
    return String(value || '')
  }

  const storageValueToInputValue = (value: unknown): INumberPropertyTypeInterfaces['inputValue'] | null => {
    if(isNumberBin(value)) {
      return value.from
    }
    return (value as INumberPropertyTypeInterfaces['storedValue']) ?? null
  }

  const searchToFilterQuery = (search: string, databaseProperties: IDatabasePropertyResponseInstance[]): IFilter => {
    return {
      type: FilterItemType.Group,
      operator: FilterGroupOperator.Or,
      items: databaseProperties.map(databaseProperty => {
        return {
          type: FilterItemType.Property,
          id: databaseProperty.id,
          subProperty: null,
          operator: {
            type: FilterPropertyOperator.Contains,
          },
          payload: {
            type: FilterPropertyPayloadType.Static,
            value: search,
          },
          isDisabled: false,
        }
      }),
    }
  }

  const toSortableNumber = (value: unknown): number | null => {
    if(isNumberBin(value)) {
      return value.from ?? -Infinity
    }
    if(value === null || value === undefined) {
      return null
    }
    return Number(value) || 0
  }

  const compareItemValues = (
    valueA: INumberPropertyTypeInterfaces['fullValue'] | INumberBin | null,
    valueB: INumberPropertyTypeInterfaces['fullValue'] | INumberBin | null,
    sortDirection: '1' | '-1',
  ): number => {
    const numA = toSortableNumber(valueA)
    const numB = toSortableNumber(valueB)

    if (numA === null && numB === null) return 0
    if (numA === null) return 1
    if (numB === null) return -1

    const comparison = numA === numB ? 0 : (numA < numB ? -1 : 1)

    return sortDirection === '1' ? comparison : -comparison
  }

  const sortGroupItems = (
    a: {fullValue: INumberPropertyTypeInterfaces['fullValue'] | INumberBin | null},
    b: {fullValue: INumberPropertyTypeInterfaces['fullValue'] | INumberBin | null},
  ): number => {
    return compareItemValues(a.fullValue, b.fullValue, '1')
  }

  const canGroupByView = (viewType: DatabaseViewType, _property: IDatabasePropertyResponseInstance) =>
    viewType === DatabaseViewType.Table

  const canMoveItemToAnotherGroup = (property: IDatabasePropertyResponseInstance | null | undefined, granularity?: GroupGranularityType | null): boolean => {
    if(property?.canBeModified === false) {
      return false
    }
    return getNumberBinSize(granularity) === null && !isCustomNumberGranularity(granularity)
  }

  const canCreateItemInGroup = (property: IDatabasePropertyResponseInstance | null | undefined, _granularity?: GroupGranularityType | null, groupValue?: unknown): boolean => {
    if(property?.canBeModified === false) {
      return false
    }
    if(groupValue && typeof groupValue === 'object' && !Array.isArray(groupValue)) {
      const bin = groupValue as Partial<INumberBin>
      if(bin.from === null) {
        return false
      }
    }
    return true
  }

  const groupGranularity = numberGroupGranularityDescriptor

  return {
    fullValueToText,
    strategyComponentType,
    getDisplayValue,
    storageValueToInputValue,
    searchToFilterQuery,
    compareItemValues,
    sortGroupItems,
    canGroupByView,
    canMoveItemToAnotherGroup,
    canCreateItemInGroup,
    groupGranularity,
    groupSort: numericGroupSort,
  }
}
